import React, { useEffect, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import PurchaseAddInsDetails from "./PurchaseAddInsDetails";
import mockApi from "./mockAddinsApi";
import { useSsoCart } from "../../../../contexts/SsoCartContext";
import WithPaymentWrapper from "../../Common/WithPaymentWrapper";
import { toast } from "react-toastify";
import { CartPageTypeEnum } from "../../../../constants/common";

const SELECTED_ADDIN_SUBSCRIPTION_STORAGE_KEY = "selectedAddInSubscriptionMap";

const persistSelectedSubscription = (addInId, selectedSubscriptionId) => {
  if (!addInId || !selectedSubscriptionId) return;
  try {
    const existingRaw = localStorage.getItem(
      SELECTED_ADDIN_SUBSCRIPTION_STORAGE_KEY,
    );
    const existing = existingRaw ? JSON.parse(existingRaw) : {};
    localStorage.setItem(
      SELECTED_ADDIN_SUBSCRIPTION_STORAGE_KEY,
      JSON.stringify({
        ...existing,
        [addInId]: selectedSubscriptionId,
      }),
    );
  } catch {
    // Ignore persistence failures and keep primary flow working
  }
};

const getPersistedSelectedSubscription = (addInId) => {
  if (!addInId) return null;
  try {
    const existingRaw = localStorage.getItem(
      SELECTED_ADDIN_SUBSCRIPTION_STORAGE_KEY,
    );
    const existing = existingRaw ? JSON.parse(existingRaw) : {};
    return existing[addInId] ?? null;
  } catch {
    return null;
  }
};

const clearPersistedSelectedSubscription = (addInId) => {
  if (!addInId) return;
  try {
    const existingRaw = localStorage.getItem(
      SELECTED_ADDIN_SUBSCRIPTION_STORAGE_KEY,
    );
    const existing = existingRaw ? JSON.parse(existingRaw) : {};
    if (Object.hasOwn(existing, addInId)) {
      delete existing[addInId];
      localStorage.setItem(
        SELECTED_ADDIN_SUBSCRIPTION_STORAGE_KEY,
        JSON.stringify(existing),
      );
    }
  } catch {
    // Ignore persistence failures and keep primary flow working
  }
};

const normalizeAddIn = (item) => {
  if (!item) return null;
  return {
    ...item,
    availableSubscriptions:
      item.availableSubscriptions || item.subscriptions || [],
  };
};

export default function PurchaseAddInsDetailsPage() {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [addIn, setAddIn] = useState(null);
  const [loading, setLoading] = useState(true);
  const {
    cart = [],
    count,
    getCartDetails,
    addToCart,
    removeFromCart,
  } = useSsoCart();

  useEffect(() => {
    const selectedRow = location?.state?.addInRow;
    if (selectedRow && Number(selectedRow.addInId) === Number(id)) {
      setAddIn(normalizeAddIn(selectedRow));
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);
    mockApi.fetchAddIns().then((data) => {
      if (!mounted) return;
      const numericId = Number(id);
      const found = (data || []).find((a) => Number(a.addInId) === numericId);
      setAddIn(normalizeAddIn(found));
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [id, location]);

  const handleBack = () => history.push("/purchase");
  const handleOpenCart = () => history.push("/purchase/cart");

  const handleSelectionChange = (addInId, selectedSubscriptionId) => {
    persistSelectedSubscription(addInId, selectedSubscriptionId);
  };

  const handleAddToCart = async (
    selectedAddIn,
    selectedSubscription,
    payload,
  ) => {
    if (!addIn) return;

    // Backward compatibility for callers that still pass (addInId, subscriptionId)
    let effectiveAddIn = selectedAddIn;
    let effectiveSubscription = selectedSubscription;

    if (selectedAddIn && typeof selectedAddIn !== "object") {
      effectiveAddIn = addIn;
      effectiveSubscription = (addIn.availableSubscriptions || []).find(
        (sub) =>
          String(sub.addInSubscriptionId) === String(selectedSubscription),
      );
    }

    const finalPayload =
      payload ||
      (effectiveAddIn?.costType === 1
        ? {
            id: effectiveAddIn?.addInId,
            costType: 1,
            price: "$0",
          }
        : {
            id: effectiveAddIn?.addInId,
            costType: effectiveAddIn?.costType,
            defaultSubscription: effectiveSubscription,
          });

    try {
      const result = await addToCart(finalPayload, CartPageTypeEnum.ADDIN);
      if (!result?.success) {
        toast.error(result?.error || "Failed to add to cart");
        return;
      }
      const selectedSubscriptionId =
        effectiveAddIn?.costType === 1
          ? "free"
          : effectiveSubscription?.addInSubscriptionId;
      persistSelectedSubscription(
        effectiveAddIn?.addInId,
        selectedSubscriptionId,
      );
      toast.success("Added to cart successfully");
      history.push("/purchase");
      // refresh cart details if available
      getCartDetails?.();
    } catch (e) {
      console.error(e);
    }
  };

  const handleUpdateCart = async (
    selectedAddIn,
    selectedSubscription,
    payload,
  ) => {
    try {
      const removed = await removeFromCart(
        { itemId: selectedAddIn?.addInId },
        CartPageTypeEnum.ADDIN,
      );
      if (!removed?.success) {
        toast.error(removed?.error || "Failed to update cart");
        return;
      }
      await handleAddToCart(selectedAddIn, selectedSubscription, payload);
    } catch (e) {
      console.error(e);
    }
    getCartDetails?.();
  };

  const handleRemoveFromCart = async (selectedAddIn) => {
    try {
      const removed = await removeFromCart(
        { itemId: selectedAddIn?.addInId },
        CartPageTypeEnum.ADDIN,
      );
      if (!removed?.success) {
        toast.error(removed?.error || "Failed to remove from cart");
        return;
      }
      clearPersistedSelectedSubscription(selectedAddIn?.addInId);
      toast.success("Removed from cart successfully");
      history.push("/purchase");
      getCartDetails?.();
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) return null;
  if (!addIn) return <div>Add-in not found</div>;

  return (
    <WithPaymentWrapper>
      <PurchaseAddInsDetails
        addIn={addIn}
        cartItems={cart}
        cartCount={count}
        initialSelectedSubscriptionId={getPersistedSelectedSubscription(
          addIn?.addInId,
        )}
        onBack={handleBack}
        onOpenCart={handleOpenCart}
        onSelectionChange={handleSelectionChange}
        onAddToCart={handleAddToCart}
        onUpdateCart={handleUpdateCart}
        onRemoveFromCart={handleRemoveFromCart}
      />
    </WithPaymentWrapper>
  );
}
